import { useMemo } from 'react';
import { useStorageState } from 'react-simplikit';

import { useSelectedSavedQuery } from './use-selected-saved-query';

import type { DateRange } from '@/entities/query';
import { getPresetDateRange, PresetName } from '@/shared/utils/get-preset-date-range';
import type { DateRangeState } from '../components/shadcn/date-range-picker/date-range-picker';
import { useResetStorage } from './use-reset';

// SharedDateRange는 기본적으로 presetName 혹은 DateRange 중 하나만 저장
// 사용상의 편의를 위해 computedDateRange제공

const SHARED_DATE_RANGE_STORAGE_RESET_KEY = 'shared-date-range-reset-key';
const SHARED_DATE_RANGE_STORAGE_RESET_VALUE = '251215';

export function useSharedDateRange(): {
  dateRange: DateRange;
  presetName: PresetName | null;
  dateRangeState: DateRangeState;
  setDateRange: (params: DateRangeState) => void;
} {
  const { query } = useSelectedSavedQuery();

  const defaultPresetName = PresetName.LAST30;

  const STORAGE_KEY = `selected-date-range-${query.id}`;

  useResetStorage({
    resetKey: SHARED_DATE_RANGE_STORAGE_RESET_KEY,
    resetValue: SHARED_DATE_RANGE_STORAGE_RESET_VALUE,
    storageKeysToReset: [STORAGE_KEY],
  });

  const [selectedDateRange, setSelectedDateRange] = useStorageState<DateRangeState>(STORAGE_KEY, {
    defaultValue: {
      presetName: defaultPresetName,
    },
  });

  const computedDateRange = useMemo(() => {
    if ('dateRange' in selectedDateRange && selectedDateRange.dateRange) {
      return selectedDateRange.dateRange;
    }
    if ('presetName' in selectedDateRange && selectedDateRange.presetName) {
      return getPresetDateRange(selectedDateRange.presetName || defaultPresetName);
    }
    return getPresetDateRange(defaultPresetName);
  }, [defaultPresetName, selectedDateRange]);

  return {
    dateRangeState: selectedDateRange,
    dateRange: computedDateRange,
    presetName: 'presetName' in selectedDateRange ? selectedDateRange.presetName : null,
    setDateRange: setSelectedDateRange,
  };
}
